import { FC, useEffect, useRef, useState } from 'react';
import { DEFAULT_VOLUME } from 'utils/constants';
import { YoutubePlaylistVideo } from 'utils/getYoutubePlaylistVideos';
import { loadYoutubeIframeApi, YTPlayer } from 'utils/loadYoutubeIframeApi';

interface VideoCarouselItemProps {
  // itemId is Consumed by react-horizontal-scrolling-menu to track item visibility and
  // drive scrollPrev/scrollNext. Required on every direct ScrollMenu child.
  // eslint-disable-next-line react/no-unused-prop-types
  itemId: string;
  video: YoutubePlaylistVideo;
  isPlaying: boolean;
  onPlay: () => void;
}

const VideoCarouselItem: FC<VideoCarouselItemProps> = ({
  video,
  isPlaying,
  onPlay,
}) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const mountRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YTPlayer | null>(null);
  const loopGuardRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!isPlaying) return;

    const mount = mountRef.current;
    if (!mount) return;

    // The YouTube API replaces its target element with an <iframe>, which
    // would desync React's tree if that target were React-rendered. Instead
    // we hand it a plain element we create and destroy ourselves inside this
    // effect, so React never tries to reconcile it.
    const target = document.createElement('div');
    mount.appendChild(target);

    let cancelled = false;

    loadYoutubeIframeApi()
      .then((YT) => {
        if (cancelled) return;
        playerRef.current = new YT.Player(target, {
          width: '100%',
          height: '100%',
          videoId: video.id,
          // Privacy-enhanced host (sends no cookies until playback).
          host: 'https://www.youtube-nocookie.com',
          playerVars: {
            autoplay: 1,
            // controls:1 is the documented default (show the scrub bar), but for
            // genuine Shorts YouTube may serve a chrome-less player that ignores
            // it -- non-deterministically. That variant also loops internally.
            // Neither `controls` nor `loop` can force/stop that; per the YouTube
            // docs `loop` only loops a single video when combined with
            // `playlist`, so loop:0 here is nominal. The real "play once" defense
            // is the loop guard (onReady) plus the ENDED handler below.
            controls: 1,
            loop: 0,
            playsinline: 1,
            rel: 0,
          },
          events: {
            onReady: (event) => {
              event.target.setVolume(DEFAULT_VOLUME);
              // Loop guard. For real Shorts, YouTube may serve a player that
              // loops seamlessly and never fires ENDED (it goes straight from
              // the end back to the start while staying in PLAYING). Poll for
              // that: if playback wraps back near the start after having reached
              // the end, pause it so the video plays through only once. (The
              // ENDED handler below covers the classic player.)
              const player = event.target;
              let reachedEnd = false;
              loopGuardRef.current = setInterval(() => {
                const duration = player.getDuration();
                if (!duration) return;
                const t = player.getCurrentTime();
                if (duration - t <= 0.4) {
                  reachedEnd = true;
                } else if (reachedEnd && t <= 1) {
                  // Wrapped back to the start -> it looped. Stop it.
                  player.pauseVideo();
                  player.seekTo(0, true);
                  if (loopGuardRef.current) {
                    clearInterval(loopGuardRef.current);
                    loopGuardRef.current = null;
                  }
                }
              }, 250);
            },
            onStateChange: (event) => {
              // Classic player path: pause on ENDED so it plays once.
              if (event.data === YT.PlayerState.ENDED) {
                event.target.pauseVideo();
              }
            },
          },
        });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('YouTube iframe API failed to load:', error);
        setFailed(true);
      });

    return () => {
      cancelled = true;
      if (loopGuardRef.current) {
        clearInterval(loopGuardRef.current);
        loopGuardRef.current = null;
      }
      playerRef.current?.destroy();
      playerRef.current = null;
      mount.replaceChildren();
    };
  }, [isPlaying, video.id]);

  // Pause playback when the video scrolls out of view (vertical page scroll or
  // horizontal carousel scroll past the viewport edge).
  useEffect(() => {
    if (!isPlaying) return;
    const item = itemRef.current;
    if (!item) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          playerRef.current?.pauseVideo();
        }
      },
      { threshold: 0 }
    );
    observer.observe(item);

    return () => observer.disconnect();
  }, [isPlaying]);

  if (failed) return null;

  return (
    <div className="video-carousel-item" ref={itemRef}>
      <div className="video-carousel-item-media">
        <div ref={mountRef} className="video-carousel-item-player-mount" />
        {!isPlaying && (
          <button
            type="button"
            className="video-carousel-item-play"
            onClick={onPlay}
            aria-label={`Play video: ${video.title}`}
          >
            <img
              src={video.thumbnailUrl}
              alt=""
              className="video-carousel-item-thumbnail"
              loading="lazy"
            />
            <span
              className="video-carousel-item-play-icon"
              aria-hidden="true"
            />
          </button>
        )}
      </div>
    </div>
  );
};

export default VideoCarouselItem;
